import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Field, StatusBadge } from "@/components/StatusBits";
import { useApp } from "@/context/AppContext";
import { api, errMsg, fmtDT } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const Box = ({ children, testId }) => <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 max-w-3xl" data-testid={testId}>{children}</div>;

const save = async (path, body, msg) => {
  try {
    const { data } = await api.put(path, body);
    toast.success(msg);
    return data;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
};

export const RulesTab = ({ settings }) => {
  const { isAdmin } = useApp();
  const [r, setR] = useState(settings.rules);
  const num = (k) => (e) => setR({ ...r, [k]: Number(e.target.value) });
  return (
    <Box testId="rules-tab">
      <p className="text-xs text-slate-500">Calls go out any time you press Call or Start. These defaults control automatic repeat calls in queue sessions (each session can override them). Do-not-call contacts are never dialed.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Default timezone" hint="Used to count attempts per day"><Input data-testid="rules-timezone" value={r.timezone} onChange={(e) => setR({ ...r, timezone: e.target.value })} disabled={!isAdmin} /></Field>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <Field label="Retry interval (min)" hint="Automatic retries after no answer"><Input type="number" data-testid="rules-retry" value={r.retry_interval_minutes} onChange={num("retry_interval_minutes")} disabled={!isAdmin} /></Field>
        <Field label="Cooldown (min)" hint="Minimum gap between any attempts"><Input type="number" data-testid="rules-cooldown" value={r.cooldown_minutes} onChange={num("cooldown_minutes")} disabled={!isAdmin} /></Field>
        <Field label="Max attempts / day"><Input type="number" data-testid="rules-daily" value={r.max_attempts_per_day} onChange={num("max_attempts_per_day")} disabled={!isAdmin} /></Field>
        <Field label="Max total attempts" hint="Sequence limit per contact"><Input type="number" data-testid="rules-total" value={r.max_total_attempts} onChange={num("max_total_attempts")} disabled={!isAdmin} /></Field>
      </div>
      {isAdmin && <Button data-testid="rules-save-btn" onClick={async () => { const d = await save("/settings/rules", r, "Calling rules saved"); if (d) setR(d); }}>Save rules</Button>}
    </Box>
  );
};

export const SmsTab = ({ settings }) => {
  const { isAdmin } = useApp();
  const [s, setS] = useState(settings.sms);
  const [nums, setNums] = useState([]);
  useEffect(() => {
    api.get("/numbers").then((r) => setNums(r.data.filter((n) => n.enabled))).catch(() => {});
  }, []);
  return (
    <Box testId="sms-tab">
      <label className="flex items-center gap-2 text-sm"><Switch data-testid="sms-auto-toggle" disabled={!isAdmin} checked={s.auto_missed_call_enabled} onCheckedChange={(v) => setS({ ...s, auto_missed_call_enabled: v })} /> Send an automatic text after a missed call</label>
      <p className="text-xs text-slate-500">Off by default. Only sent to contacts with SMS permission, never after STOP, at most once per 24 hours, and paused automatically once the contact replies.</p>
      <Field label="Send from">
        <Select value={s.from_number || ""} onValueChange={(v) => setS({ ...s, from_number: v })} disabled={!isAdmin}>
          <SelectTrigger data-testid="sms-from-select" className="bg-white"><SelectValue placeholder="Choose an enabled number" /></SelectTrigger>
          <SelectContent className="bg-white">{nums.map((n) => <SelectItem key={n.sid} value={n.phone_number}>{n.phone_number}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Missed-call template"><Textarea rows={3} data-testid="sms-template" value={s.missed_call_template} onChange={(e) => setS({ ...s, missed_call_template: e.target.value })} disabled={!isAdmin} /></Field>
      {isAdmin && <Button data-testid="sms-save-btn" onClick={async () => { const d = await save("/settings/sms", s, "SMS settings saved"); if (d) setS(d); }}>Save</Button>}
    </Box>
  );
};

export const BusinessTab = ({ settings }) => {
  const { isAdmin } = useApp();
  const [name, setName] = useState(settings.general.business_name);
  return (
    <Box testId="business-tab">
      <Field label="Business name" hint="Used in scripts as {business_name} and by the AI caller to introduce itself"><Input data-testid="business-name-input" value={name} onChange={(e) => setName(e.target.value)} disabled={!isAdmin} placeholder="[Your business name]" /></Field>
      {isAdmin && <Button data-testid="business-save-btn" onClick={() => save("/settings/general", { business_name: name }, "Saved")}>Save</Button>}
    </Box>
  );
};

export const ProfileTab = () => {
  const { me, setMe } = useApp();
  const [p, setP] = useState({ call_target: me.call_target || "browser", agent_phone: me.agent_phone || "" });
  return (
    <Box testId="profile-tab">
      <Field label="Where should queue calls reach you?">
        <Select value={p.call_target} onValueChange={(v) => setP({ ...p, call_target: v })}>
          <SelectTrigger data-testid="profile-target-select" className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="browser">This browser (softphone)</SelectItem><SelectItem value="phone">My agent phone</SelectItem></SelectContent>
        </Select>
      </Field>
      <Field label="Agent phone (alternative destination)"><Input data-testid="profile-agent-phone" value={p.agent_phone} onChange={(e) => setP({ ...p, agent_phone: e.target.value })} placeholder="+15551234567" /></Field>
      <Button data-testid="profile-save-btn" onClick={async () => { const d = await save("/me", p, "Profile saved"); if (d) setMe(d); }}>Save</Button>
    </Box>
  );
};

const Authorized = () => {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("agent");
  useEffect(() => {
    api.get("/access/authorized").then((r) => setList(r.data)).catch(() => {});
  }, []);
  const add = async () => {
    try {
      setList((await api.post("/access/authorized", { email, role })).data);
      setEmail("");
      toast.success("Account authorized");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3 max-w-4xl" data-testid="authorized-accounts">
      <div>
        <h3 className="font-heading font-semibold">Authorized accounts</h3>
        <p className="text-xs text-slate-500">These Gmail / Google or email accounts get access automatically when they sign in. Anyone else waits as pending until you approve them below. Removing an entry doesn't remove someone who already signed in — change their role below instead.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input data-testid="authorized-email-input" className="max-w-xs" placeholder="name@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="authorized-role-select" className="w-32 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="agent">agent</SelectItem><SelectItem value="admin">admin</SelectItem></SelectContent>
        </Select>
        <Button data-testid="authorized-add-btn" disabled={!email.includes("@")} onClick={add}>Authorize</Button>
      </div>
      {list.map((a) => (
        <div key={a.email} className="flex items-center gap-3 text-sm border-t border-slate-100 pt-2" data-testid={`authorized-row-${a.email}`}>
          <span>{a.email}</span><StatusBadge tone="info" value="x">{a.role}</StatusBadge>
          <button className="ml-auto text-xs text-rose-600" data-testid={`authorized-remove-${a.email}`} onClick={() => api.delete(`/access/authorized/${encodeURIComponent(a.email)}`).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)))}>Remove</button>
        </div>
      ))}
    </div>
  );
};

export const TeamTab = () => {
  const [data, setData] = useState({ roles: [], users: [] });
  const load = () => api.get("/users").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  const setRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
    <Authorized />
    <div className="bg-white border border-slate-200 rounded-lg max-w-4xl" data-testid="team-tab">
      <p className="text-xs text-slate-500 p-4">New sign-ups wait as "pending" with no access until you approve them as an agent or admin.</p>
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Availability</TableHead><TableHead>Last seen</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.users.map((u) => (
            <TableRow key={u.user_id} className="text-sm" data-testid={`team-row-${u.user_id}`}>
              <TableCell className="py-2">{u.name}</TableCell><TableCell className="py-2 text-xs">{u.email}</TableCell>
              <TableCell className="py-2"><StatusBadge value={u.availability || "offline"} /></TableCell>
              <TableCell className="py-2 text-xs">{fmtDT(u.last_seen_at)}</TableCell>
              <TableCell className="py-2">
                <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                  <SelectTrigger data-testid={`team-role-${u.user_id}`} className="h-8 w-32 bg-white text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{data.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </div>
  );
};
